import { MEDIA_FILE_TYPES } from "../types";
import { z } from "zod";

export const postFiltersSchema = z
  .object({
    categoryName: z.string(),
    subcategoryNames: z.string().array(),
    files: z
      .object({ nFiles: z.number().gt(0) })
      .strict()
      .partial(),

    seedsReceived: z
      .object({ minSeeds: z.number().gt(0), maxSeeds: z.number().gt(0) })
      .strict()
      .partial()
      .superRefine((data, ctx) => {
        if (!data.maxSeeds || !data.minSeeds) {
          return;
        }

        if (data.maxSeeds < data.minSeeds) {
          ctx.addIssue({
            code: z.ZodIssueCode.too_small,
            minimum: data.minSeeds,
            type: "number",
            inclusive: true,
            message: "maxSeeds cannot be less than minSeeds",
          });
        }
      }),

    collaborators: z.object({ nCollaborators: z.number().gte(0) }).strict(),
    typeOfFileFormat: z
      .object({
        fileFormats: z
          .enum([
            "MOV",
            "MP4",
            "WEBM",
            "JPG",
            "JPEG",
            "JFIF",
            "PNG",
            "GIF",
            "BLOB",
          ])
          .array(),
      })
      .strict(),

    projectTimeline: z
      .object({
        timelineStart: z.string().datetime(),
        timelineEnd: z.string().datetime(),
      })
      .strict()
      .superRefine((data, ctx) => {
        const timelineStart = new Date(data.timelineStart);
        const timelineEnd = new Date(data.timelineEnd);

        if (timelineEnd <= timelineStart) {
          ctx.addIssue({
            code: z.ZodIssueCode.invalid_date,
            message: "timelineEnd must be a datetime after timelineStart",
          });
        }
      }),
    projectDuration: z
      .object({
        hoursGreaterThan: z.number(),
        hoursLowerThan: z.number().nullable(),
      })
      .strict(),
    startDate: z.object({ startDate: z.string().datetime() }).strict(),
    completionDate: z
      .object({ completionDate: z.string().datetime() })
      .strict(),
    from: z.any(),
    dynamicFilters: z.record(z.string(), z.string()).optional(),
  })
  .partial()
  .strict();

export const peopleFiltersSchema = z
  .object({
    categoryName: z.string(),
    subcategoryNames: z.string().array(),
    location: z.string(),

    seedsReceived: z
      .object({ minSeeds: z.number().gt(0), maxSeeds: z.number().gt(0) })
      .strict()
      .partial()
      .superRefine((data, ctx) => {
        if (!data.maxSeeds || !data.minSeeds) {
          return;
        }

        if (data.maxSeeds < data.minSeeds) {
          ctx.addIssue({
            code: z.ZodIssueCode.too_small,
            minimum: data.minSeeds,
            type: "number",
            inclusive: true,
            message: "maxSeeds cannot be less than minSeeds",
          });
        }
      }),

    seedsDonated: z
      .object({ minSeeds: z.number().gt(0), maxSeeds: z.number().gt(0) })
      .strict()
      .partial()
      .superRefine((data, ctx) => {
        if (!data.maxSeeds || !data.minSeeds) {
          return;
        }

        if (data.maxSeeds < data.minSeeds) {
          ctx.addIssue({
            code: z.ZodIssueCode.too_small,
            minimum: data.minSeeds,
            type: "number",
            inclusive: true,
            message: "maxSeeds cannot be less than minSeeds",
          });
        }
      }),

    // these two are not finalized
    skillLevel: z.string(),
    availability: z.string(),
    from: z.any(),
    dynamicFilters: z.record(z.string(), z.string()).optional(),
  })
  .partial()
  .strict();

export const galleryFiltersSchema = z
  .object({
    categoryName: z.string(),
    subcategoryNames: z.string().array(),
    galleryType: z.enum(["talents", "posts"]),
    lastModified: z.string().datetime(),

    contributors: z.object({ nCollaborators: z.number().gte(0) }).strict(),

    itemCount: z.number().gt(0),
  })
  .partial()
  .strict();

export const postForYou = {
  body: z.object({
    filters: postFiltersSchema,
  }),
};

export type PostFiltersSchema = z.infer<typeof postFiltersSchema>;
export type PeopleFiltersSchema = z.infer<typeof peopleFiltersSchema>;
export type GalleryFiltersSchema = z.infer<typeof galleryFiltersSchema>;
